import React, { useState } from 'react';
import Card from '../../components/card';
import Title from '../../components/title';
import {
  IOrder,
  ISearchOrderReq,
  ISearchOrderRes,
} from '../../interfaces/order';
import {
  Button,
  DatePicker,
  Input,
  Select,
  TablePaginationConfig,
  notification,
} from 'antd';
import { PlusOutlined, PrinterFilled } from '@ant-design/icons';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  exportExpressSaleAccount,
  getOrder,
  searchOrders,
} from '../../adapters/order';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import OrderTable from './components/order-table';
import locale from 'antd/es/date-picker/locale/th_TH';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { getOrderStatusOptions } from '@/helpers/orderHelper';
import { isFullAccess } from '@/utils/permissionUtils';
import { AxiosResponse } from 'axios';
import { saveAs } from 'file-saver';
import { EOrderType } from '@/enums/EOrder';
import { OrderMenu } from '@/configs/menuItems';
import { getPathSegment } from '@/utils/pathUtils';
import SubMenu from '@/components/menu/sub-menu';

import OrderMultiPrint from './maintain/components/order-info/components/order-multi-print';

const { Search } = Input;

const _criteria: ISearchOrderReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
  orderMainType: EOrderType.INTERNAL,
};

const getExportFileName = (disposition?: string) => {
  if (!disposition) {
    return `express-sale-account-${dayjs().format('YYYY-MM-DD')}.xlsx`;
  }

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
  if (asciiMatch?.[1]) {
    return asciiMatch[1];
  }

  return `express-sale-account-${dayjs().format('YYYY-MM-DD')}.xlsx`;
};

const Order = () => {
  const pathSegment = getPathSegment();
  const navigate = useNavigate();
  const perFullAccess = isFullAccess();

  const [criteria, setCriteria] = useState<ISearchOrderReq>({
    ..._criteria,
  });
  const [selectedRow, setSelectedRow] = useState<IOrder[]>([]);
  const [isExportingExpress, setIsExportingExpress] = useState(false);

  const results = useQueries({
    queries: selectedRow.map(order => ({
      queryKey: ['get-order', order.id],
      queryFn: () => getOrder(Number(order.id)),
      enabled: !!order.id,
    })),
  });
  const orderSelectedRow = results
    .map(r => r.data)
    .filter((d): d is IOrder => !!d);

  const refetchAll = async () => {
    await Promise.all(results.map(r => r.refetch()));
  };

  const { data, refetch, isLoading, isFetching } = useQuery<
    AxiosResponse<ISearchOrderRes>,
    Error
  >({
    queryKey: ['search-order', criteria],
    queryFn: async () => await searchOrders(criteria),
  });

  const onActionOrder = () => {
    refetch();
  };

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IOrder> | SorterResult<IOrder>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof IOrder) || newCriteria.orderBy;
      newCriteria.orderType = sorter.order || newCriteria.orderType;
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      orderBy: 'id',
      orderType: 'descend',
      pageStart: 1,
      pageSize: 10,
    });
  };

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: 'descend',
        pageStart: 1,
        pageSize: 10,
        startDate: dates[0]?.toDate(),
        endDate: dates[1]?.toDate(),
      });
    } else {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: 'descend',
        pageStart: 1,
        pageSize: 10,
        startDate: null,
        endDate: null,
      });
    }
  };

  const handleSelectedOrders = (orders: IOrder[]) => {
    setSelectedRow(prevSelected => {
      const newOrderIds = orders.map(o => o.id);
      const existingOrders = prevSelected.filter(
        o => !newOrderIds.includes(o.id),
      );
      return [...existingOrders, ...orders];
    });
  };

  const handleExportExpress = async () => {
    try {
      setIsExportingExpress(true);
      const response = await exportExpressSaleAccount(criteria);
      const blob = new Blob([response.data], {
        type:
          response.headers['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const fileName = getExportFileName(
        response.headers['content-disposition'],
      );
      saveAs(blob, fileName);

      notification.success({
        message: 'Export to Express สำเร็จ',
      });
      refetch();
    } catch (error) {
      console.log(error);
    } finally {
      setIsExportingExpress(false);
    }
  };

  return (
    <>
      <SubMenu active={pathSegment} items={OrderMenu} />
      <Card>
        <Title title="จัดการออเดอร์">
          <div className="flex justify-end gap-3">
            <Button
              onClick={handleExportExpress}
              loading={isExportingExpress}
              icon={<PrinterFilled />}
            >
              Export Express
            </Button>
            {perFullAccess && (
              <Button type="primary" onClick={() => navigate(`/order/create`)}>
                <PlusOutlined /> เพิ่มใหม่
              </Button>
            )}
          </div>
        </Title>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <div className="flex flex-col md:flex-row justify-between gap-3">
              <DatePicker.RangePicker
                size="large"
                locale={locale}
                onChange={onRangeChange}
              />
              <Select
                allowClear
                className="md:min-w-[150px]"
                size="large"
                placeholder="เลือก สถานะ"
                options={getOrderStatusOptions()}
                value={criteria?.status}
                onChange={e =>
                  setCriteria({
                    ...criteria,
                    status: e,
                  })
                }
              />
              <Search
                className="md:max-w-[400px] md:min-w-[400px]"
                placeholder="ค้นหา"
                allowClear
                size="large"
                enterButton="ค้นหา"
                onSearch={onSearch}
              />
            </div>
          </div>
          <OrderTable
            criteria={criteria}
            order={data?.data || { data: [], totalRecords: 0 }}
            loading={isLoading || isFetching}
            onEditData={item => console.log(item)}
            onDelete={onActionOrder}
            onTableChange={onTableChange}
            onRowSelected={handleSelectedOrders}
          />
        </div>
      </Card>
      {selectedRow.length > 0 && (
        <OrderMultiPrint
          data={orderSelectedRow}
          afterPrint={refetchAll}
          onCancel={() => setSelectedRow([])}
        />
      )}
    </>
  );
};

export default Order;
